import React, { useEffect, useState, useRef } from "react";
import { Paper, Box, Typography, Grid, LinearProgress } from "@mui/material";

/**
 * A reusable component for displaying processing or upload status
 * with background freeze effect
 * 
 * @param {Object} props - Component props
 * @param {number} props.progress - Processing progress percentage (0-100)
 * @param {Object} props.stats - Processing statistics object
 * @param {number} props.stats.processed - Number of processed records
 * @param {number} props.stats.total - Total number of records
 * @param {string} props.stats.timeElapsed - Elapsed time (formatted string)
 * @param {string} props.stats.timeRemaining - Remaining time (formatted string)
 * @param {string} props.status - Status text to display ("Uploading" or "Processing") - defaults to "Processing"
 * @param {boolean} props.hideTimeRemaining - Whether to hide the Time Remaining field
 * @param {Object} props.theme - Theme object
 * @param {Object} props.sx - Additional styles for Paper component
 */
const CLoader = ({
  progress = 0,
  stats = {
    processed: 0,
    total: 0,
    timeElapsed: "0:00",
    timeRemaining: "0:00",
  },
  status = "Processing",
  hideTimeRemaining = false,
  theme,
  sx = {},
}) => {
  // Keep track of the highest progress value seen so far to prevent progress from going backward
  const [displayProgress, setDisplayProgress] = useState(progress);
  const prevProgressRef = useRef(progress);
  
  // Determine if we're in upload or processing mode
  const isUploading = status === "Uploading";

  // Update displayProgress when progress increases, never decrease
  useEffect(() => {
    if (progress > prevProgressRef.current) {
      setDisplayProgress(progress);
      prevProgressRef.current = progress;
    }
  }, [progress]);

  // Add body style when component mounts to prevent scrolling
  useEffect(() => {
    // Store original body style
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // Disable scrolling on body
    document.body.style.overflow = "hidden";

    // Cleanup function to restore original style when component unmounts
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Determine grid column sizes based on whether Time Remaining is shown
  const gridSize = hideTimeRemaining ? 4 : 3;

  return (
    <>
      {/* Overlay that covers the entire viewport */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backdropFilter: "blur(3px)"
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            mb: 3,
            maxWidth: "600px",
            width: "90%",
            bgcolor: theme?.palette?.background?.paper || "#fff",
            borderColor: theme?.palette?.primary?.light || "#ccc",
            borderWidth: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            ...sx,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              {isUploading ? "Uploading file..." : "Processing in progress..."}
            </Typography>
          </Box>

          <Box sx={{ width: "100%", mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={displayProgress}
              color="secondary"
              sx={{
                height: 10,
                borderRadius: 5,
                '& .MuiLinearProgress-bar': {
                  transition: 'transform 0.3s ease-out' // Smooth transition for progress updates
                }
              }}
            />
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} sm={isUploading ? 3 : gridSize}>
              <Typography variant="body2" color="textSecondary">
                Progress:
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {displayProgress}%
              </Typography>
            </Grid>

            {!isUploading && (
              <>
                <Grid item xs={6} sm={gridSize}>
                  <Typography variant="body2" color="textSecondary">
                    Processed:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.processed}/{stats.total}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={gridSize}>
                  <Typography variant="body2" color="textSecondary">
                    Time Elapsed:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {stats.timeElapsed}
                  </Typography>
                </Grid>
                {!hideTimeRemaining && (
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Time Remaining:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {stats.timeRemaining}
                    </Typography>
                  </Grid>
                )}
              </>
            )}

            {isUploading && (
              <Grid item xs={6} sm={9}>
                <Typography variant="body2" color="textSecondary">
                  Status:
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  In progress...
                </Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>
    </>
  );
};

export default CLoader;